import { useState, useEffect, useCallback } from 'react';
import { Button } from '~/components/ui/button';
import { Input } from '~/components/ui/input';
import { Label } from '~/components/ui/label';
import { Checkbox } from '~/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '~/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select';

interface TermOption {
  id: string;
  name: string;
}

interface CourseData {
  id?: string;
  name: string;
  subtitle: string;
  catalogCode: string;
  description: string;
  defaultSections: number;
  gradeLevelKeys: string[];
  termIds: string[];
}

interface CourseFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (data: CourseData) => Promise<void>;
  course?: {
    id: string;
    name: string;
    subtitle: string | null;
    catalogCode: string | null;
    description: string | null;
    defaultSections: number;
    gradeLevelKeys: string[];
    terms: { id: string; name: string }[];
  } | null;
  availableTerms: TermOption[];
  availableGradeLevels: string[];
  onConfigureTerms?: () => void;
}

const EMPTY_FORM: CourseData = {
  name: '',
  subtitle: '',
  catalogCode: '',
  description: '',
  defaultSections: 1,
  gradeLevelKeys: [],
  termIds: [],
};

export default function CourseFormDialog({
  open,
  onOpenChange,
  onSave,
  course,
  availableTerms,
  availableGradeLevels,
  onConfigureTerms,
}: CourseFormDialogProps) {
  const [form, setForm] = useState<CourseData>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isEdit = !!course?.id;

  useEffect(() => {
    if (open) {
      setError(null);
      if (course) {
        setForm({
          id: course.id,
          name: course.name,
          subtitle: course.subtitle ?? '',
          catalogCode: course.catalogCode ?? '',
          description: course.description ?? '',
          defaultSections: course.defaultSections,
          gradeLevelKeys: [...course.gradeLevelKeys],
          termIds: course.terms.map((t) => t.id),
        });
      } else {
        setForm({ ...EMPTY_FORM, termIds: availableTerms.map((t) => t.id) });
      }
    }
  }, [open, course, availableTerms]);

  const handleSubmit = useCallback(async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await onSave(form);
      onOpenChange(false);
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : 'Unable to save the course.');
    } finally {
      setSaving(false);
    }
  }, [form, onSave, onOpenChange]);

  const toggleGradeLevel = useCallback((level: string) => {
    setForm((prev) => ({
      ...prev,
      gradeLevelKeys: prev.gradeLevelKeys.includes(level)
        ? prev.gradeLevelKeys.filter((g) => g !== level)
        : [...prev.gradeLevelKeys, level],
    }));
  }, []);

  const toggleTerm = useCallback((termId: string) => {
    setForm((prev) => ({
      ...prev,
      termIds: prev.termIds.includes(termId)
        ? prev.termIds.filter((t) => t !== termId)
        : [...prev.termIds, termId],
    }));
  }, []);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Course' : 'Create Course'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="course-name">Name *</Label>
            <Input
              id="course-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              placeholder="e.g., Algebra 1"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="course-subtitle">Subtitle</Label>
            <Input
              id="course-subtitle"
              value={form.subtitle}
              onChange={(e) => setForm((f) => ({ ...f, subtitle: e.target.value }))}
              placeholder="e.g., Modern Times"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="course-code">Catalog Code</Label>
              <Input
                id="course-code"
                value={form.catalogCode}
                onChange={(e) => setForm((f) => ({ ...f, catalogCode: e.target.value }))}
                placeholder="e.g., ALG-101"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="course-sections">Sections</Label>
              <Select
                value={String(form.defaultSections)}
                onValueChange={(v) => setForm((f) => ({ ...f, defaultSections: Number(v) }))}
              >
                <SelectTrigger id="course-sections">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <SelectItem key={n} value={String(n)}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="course-description">Description</Label>
            <Input
              id="course-description"
              value={form.description}
              onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
              placeholder="Optional description"
            />
          </div>

          {availableGradeLevels.length > 0 && (
            <div className="space-y-2">
              <Label>Grade Levels</Label>
              <div className="flex flex-wrap gap-3">
                {availableGradeLevels.map((level) => (
                  <label key={level} className="flex items-center gap-1.5 text-sm">
                    <Checkbox
                      checked={form.gradeLevelKeys.includes(level)}
                      onCheckedChange={() => toggleGradeLevel(level)}
                    />
                    {level}
                  </label>
                ))}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label>Active Terms</Label>
            {availableTerms.length > 0 ? (
              <>
                <div className="flex flex-wrap gap-3">
                  {availableTerms.map((term) => (
                    <label key={term.id} className="flex items-center gap-1.5 text-sm">
                      <Checkbox
                        checked={form.termIds.includes(term.id)}
                        onCheckedChange={() => toggleTerm(term.id)}
                      />
                      {term.name}
                    </label>
                  ))}
                </div>
                {isEdit && (
                  <p className="text-xs text-muted-foreground">
                    Unchecking a term removes only empty class instances. Current or former student
                    enrollment history prevents removal; other class data must be removed first.
                  </p>
                )}
              </>
            ) : (
              <div className="rounded-md border border-dashed p-3 space-y-2">
                <p className="text-sm text-muted-foreground">
                  This school year has no academic terms. The course can be saved now, but classes
                  cannot be created until at least one term is configured.
                </p>
                {onConfigureTerms && (
                  <Button type="button" variant="outline" size="sm" onClick={onConfigureTerms}>
                    Configure Academic Terms
                  </Button>
                )}
              </div>
            )}
          </div>

          {error && (
            <div role="alert" className="rounded-md border border-destructive/50 bg-destructive/10 p-3 text-sm text-destructive">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || !form.name.trim()}>
            {saving ? 'Saving...' : isEdit ? 'Save Changes' : 'Create Course'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
